import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { ProviderCategory } from '~/types';
import { getData } from '~/utils/service';

type CategoryLookup = {
  // Undefined while pending or after failure; [] is a successfully empty lookup.
  categories: ProviderCategory[] | undefined;
  loadCategories: () => Promise<ProviderCategory[]>;
};
const ProviderCategoriesContext = createContext<CategoryLookup | undefined>(undefined);

// Requests and results belong to this page instance. Search, filter typing and
// selection share them; remount refreshes and a rejected request permits retry.
export const ListingProviderCategories = ({ children }: { children: ReactNode }) => {
  const [categories, setCategories] = useState<ProviderCategory[]>();
  const request = useRef<Promise<ProviderCategory[]> | undefined>(undefined);
  const active = useRef(true);
  const loadCategories = useCallback(() => {
    if (!request.current) {
      request.current = getData<ProviderCategory[]>('providerCategories')
        .then((data) => {
          if (active.current) setCategories(data);
          return data;
        })
        .catch((error: unknown) => {
          request.current = undefined;
          throw error;
        });
    }
    return request.current;
  }, []);
  useEffect(() => {
    active.current = true;
    void loadCategories().catch(() => {
      /* The picker can retry; labels use ID fallbacks. */
    });
    return () => {
      active.current = false;
    };
  }, [loadCategories]);
  return (
    <ProviderCategoriesContext.Provider value={{ categories, loadCategories }}>
      {children}
    </ProviderCategoriesContext.Provider>
  );
};

export const useListingProviderCategories = () => useContext(ProviderCategoriesContext);
